import type {
  CriterionLevel,
  DevAction,
  DevDomainConfig,
  DevDomainId,
  DevHistoryEvent,
  DevStage,
  DevState,
  ReferralDraft,
  ReferralStatus,
  ReviewDraft,
  Round,
} from '../types/developmentDemo'
import { formatId, nowIso } from './demoStorage'

export const devStorageKey = (id: DevDomainId) => `noetivra.demo.${id}.v1`

export const stageLabels: Record<DevStage, { label: string; participant: string; reviewer: string }> = {
  'ready-initial': {
    label: 'Ready to begin',
    participant: 'Read the task and submit your response.',
    reviewer: 'Waiting for a first response.',
  },
  'awaiting-review': {
    label: 'Awaiting review',
    participant: 'Your response has been submitted and is waiting for review.',
    reviewer: 'A response is ready for evidence review.',
  },
  revision: {
    label: 'Revision requested',
    participant: 'Read the feedback and submit a revised response. Your earlier response is kept.',
    reviewer: 'Waiting for a revised response.',
  },
  'awaiting-activity': {
    label: 'Review complete',
    participant: 'Your response was reviewed. A development activity will be assigned.',
    reviewer: 'Assign a development activity linked to the review.',
  },
  practising: {
    label: 'Practising',
    participant: 'Complete your development activity and record a reflection.',
    reviewer: 'Development activity in progress.',
  },
  'ready-reassessment': {
    label: 'Ready to reassess',
    participant: 'Practice is complete. Take the follow-up scenario.',
    reviewer: 'Waiting for the reassessment response.',
  },
  'cycle-complete': {
    label: 'Cycle reviewed',
    participant: 'Both rounds are reviewed. Compare them in My Progress.',
    reviewer: 'Both rounds are reviewed. Compare them in Progress.',
  },
}

export const referralStatusLabels: Record<ReferralStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  delivered: 'Delivered',
  declined: 'Declined',
}

export const decisionLabels = {
  'follow-up': 'Request follow-up',
  advance: 'Advance to next stage',
  'not-advance': 'Do not advance',
} as const

export function participantName(config: DevDomainConfig, id: string) {
  return config.participants.find((participant) => participant.id === id)?.name ?? id
}

export const submissionsFor = (state: DevState, participantId: string) =>
  state.submissions.filter((submission) => submission.participantId === participantId)

export const reviewForSubmission = (state: DevState, submissionId: string) =>
  state.reviews.find((review) => review.submissionId === submissionId)

export const activityForReview = (state: DevState, reviewId: string) =>
  state.activities.find((activity) => activity.reviewId === reviewId)

export function deriveStage(state: DevState, participantId: string): DevStage {
  const submissions = submissionsFor(state, participantId)
  const latest = submissions[submissions.length - 1]
  if (!latest) return 'ready-initial'
  const review = reviewForSubmission(state, latest.id)
  if (!review) return 'awaiting-review'
  if (review.outcome === 'revision-requested') return 'revision'
  if (latest.round === 'reassessment') return 'cycle-complete'
  const activity = activityForReview(state, review.id)
  if (!activity) return 'awaiting-activity'
  return activity.status === 'completed' ? 'ready-reassessment' : 'practising'
}

/** The round a participant may answer now, or null when no response is allowed. */
export function openRound(state: DevState, participantId: string): Round | null {
  const stage = deriveStage(state, participantId)
  if (stage === 'ready-initial') return 'initial'
  if (stage === 'ready-reassessment') return 'reassessment'
  if (stage === 'revision') {
    const submissions = submissionsFor(state, participantId)
    return submissions[submissions.length - 1].round
  }
  return null
}

/** Latest reviewed (not revision-requested) review for each round. */
export function reviewedRounds(state: DevState, participantId: string) {
  const result: Partial<Record<Round, { submissionId: string; reviewId: string }>> = {}
  submissionsFor(state, participantId).forEach((submission) => {
    const review = reviewForSubmission(state, submission.id)
    if (review?.outcome === 'reviewed') result[submission.round] = { submissionId: submission.id, reviewId: review.id }
  })
  return result
}

export function emptyReviewDraft(config: DevDomainConfig): ReviewDraft {
  return {
    observations: Object.fromEntries(config.criteria.map((criterion) => [criterion.id, { level: '', observation: '' }])),
    feedback: '',
    openQuestions: '',
    outcome: '',
  }
}

export function validateReviewDraft(config: DevDomainConfig, draft: ReviewDraft) {
  const errors: Record<string, string> = {}
  config.criteria.forEach((criterion) => {
    const entry = draft.observations[criterion.id]
    if (!entry?.level) errors[`${criterion.id}-level`] = 'Choose what this task evidenced.'
    if (!entry?.observation.trim()) errors[`${criterion.id}-observation`] = 'Record an observation for this criterion.'
  })
  if (!draft.feedback.trim()) errors.feedback = 'Write feedback explaining the evidence.'
  if (!draft.outcome) errors.outcome = 'Choose a review outcome.'
  return errors
}

export function validateReferralDraft(draft: ReferralDraft) {
  const errors: Partial<Record<keyof ReferralDraft, string>> = {}
  if (!draft.participantId) errors.participantId = 'Choose a participant.'
  if (!draft.partnerId) errors.partnerId = 'Choose the receiving organization.'
  if (!draft.purpose.trim()) errors.purpose = 'State the purpose of the referral.'
  if (!draft.sharedInfo.trim()) errors.sharedInfo = 'Write exactly what will be shared.'
  if (!draft.consent) errors.consent = 'Confirm the participant agreed to this purpose and sharing.'
  return errors
}

export function createInitialDevState(config: DevDomainConfig): DevState {
  const { submissions, reviews, activities } = config.seed
  const events: Omit<DevHistoryEvent, 'id'>[] = [
    ...submissions.map((submission) => ({
      domain: config.id,
      participantId: submission.participantId,
      action: `${participantName(config, submission.participantId)} submitted a response to ${submission.taskVersionId} · sample record`,
      at: submission.at,
    })),
    ...reviews.map((review) => ({
      domain: config.id,
      participantId: review.participantId,
      action: `${review.reviewerRole} reviewed ${review.submissionId} · sample record`,
      at: review.at,
    })),
  ]
  const history = events.map((event, index) => ({ ...event, id: formatId('EVT', index + 1) })).reverse()
  const first = config.participants[0].id
  return {
    submissions: submissions.map((item) => ({ ...item })),
    reviews: reviews.map((item) => ({ ...item })),
    activities: activities.map((item) => ({ ...item })),
    decisions: [],
    referrals: [],
    history,
    counters: {
      submission: submissions.length,
      review: reviews.length,
      activity: activities.length,
      decision: 0,
      referral: 0,
      event: history.length,
    },
    ui: {
      workspace: null,
      lastSection: { participant: 'overview', reviewer: 'overview' },
      actingParticipantId: first,
      selectedParticipantId: first,
      selectedSubmissionId: null,
      responseDrafts: {},
      reviewDrafts: {},
      activityDrafts: {},
      reflectionDrafts: {},
      decisionDrafts: {},
      referralDraft: { participantId: '', partnerId: '', purpose: '', sharedInfo: '', consent: false },
      referralUpdateDrafts: {},
    },
  }
}

export function createDevReducer(config: DevDomainConfig) {
  return function devReducer(state: DevState, action: DevAction): DevState {
    const ui = state.ui
    const log = (current: DevState, participantId: string | undefined, text: string, at: string): DevState => {
      const next = current.counters.event + 1
      return {
        ...current,
        history: [{ id: formatId('EVT', next), domain: config.id, participantId, action: text, at }, ...current.history],
        counters: { ...current.counters, event: next },
      }
    }

    switch (action.type) {
      case 'enterWorkspace':
        return { ...state, ui: { ...ui, workspace: action.workspace } }
      case 'setSection':
        if (!ui.workspace) return state
        return { ...state, ui: { ...ui, lastSection: { ...ui.lastSection, [ui.workspace]: action.section } } }
      case 'setActingParticipant':
        return { ...state, ui: { ...ui, actingParticipantId: action.id } }
      case 'selectParticipant': {
        const lastSection = action.section && ui.workspace ? { ...ui.lastSection, [ui.workspace]: action.section } : ui.lastSection
        return { ...state, ui: { ...ui, selectedParticipantId: action.id, lastSection } }
      }
      case 'selectSubmission':
        return { ...state, ui: { ...ui, selectedSubmissionId: action.id } }
      case 'setResponseDraft':
        return { ...state, ui: { ...ui, responseDrafts: { ...ui.responseDrafts, [action.participantId]: action.text } } }
      case 'submitResponse': {
        const round = openRound(state, action.participantId)
        const text = (ui.responseDrafts[action.participantId] ?? '').trim()
        if (!round || !text) return state
        const at = nowIso()
        const number = state.counters.submission + 1
        const id = formatId('SUB', number)
        const task = config.tasks[round]
        const responseDrafts = { ...ui.responseDrafts }
        delete responseDrafts[action.participantId]
        return log(
          {
            ...state,
            submissions: [...state.submissions, { id, participantId: action.participantId, taskVersionId: task.id, round, response: text, at }],
            counters: { ...state.counters, submission: number },
            ui: { ...ui, responseDrafts },
          },
          action.participantId,
          `${participantName(config, action.participantId)} submitted ${id} (${round === 'initial' ? 'initial task' : 'reassessment'}, ${task.id})`,
          at,
        )
      }
      case 'setReviewDraft':
        return { ...state, ui: { ...ui, reviewDrafts: { ...ui.reviewDrafts, [action.submissionId]: action.draft } } }
      case 'submitReview': {
        const submission = state.submissions.find((item) => item.id === action.submissionId)
        const draft = ui.reviewDrafts[action.submissionId]
        if (!submission || !draft || reviewForSubmission(state, submission.id)) return state
        if (Object.keys(validateReviewDraft(config, draft)).length > 0 || !draft.outcome) return state
        const at = nowIso()
        const number = state.counters.review + 1
        const id = formatId('REV', number)
        const reviewDrafts = { ...ui.reviewDrafts }
        delete reviewDrafts[action.submissionId]
        return log(
          {
            ...state,
            reviews: [
              ...state.reviews,
              {
                id,
                submissionId: submission.id,
                participantId: submission.participantId,
                observations: config.criteria.map((criterion) => ({
                  criterionId: criterion.id,
                  level: draft.observations[criterion.id].level as CriterionLevel,
                  observation: draft.observations[criterion.id].observation.trim(),
                })),
                feedback: draft.feedback.trim(),
                openQuestions: draft.openQuestions.trim(),
                outcome: draft.outcome,
                reviewerRole: config.reviewerRole,
                at,
              },
            ],
            counters: { ...state.counters, review: number },
            ui: { ...ui, reviewDrafts },
          },
          submission.participantId,
          `${config.reviewerRole} recorded ${id} for ${submission.id}: ${draft.outcome === 'reviewed' ? 'Reviewed' : 'Revision requested'}`,
          at,
        )
      }
      case 'setActivityDraft':
        return { ...state, ui: { ...ui, activityDrafts: { ...ui.activityDrafts, [action.reviewId]: action.draft } } }
      case 'assignActivity': {
        const review = state.reviews.find((item) => item.id === action.reviewId)
        const submission = review && state.submissions.find((item) => item.id === review.submissionId)
        const draft = ui.activityDrafts[action.reviewId] ?? config.suggestedActivity
        if (!review || !submission || review.outcome !== 'reviewed' || submission.round !== 'initial') return state
        if (activityForReview(state, review.id) || !draft.title.trim() || !draft.instructions.trim()) return state
        const at = nowIso()
        const number = state.counters.activity + 1
        const id = formatId('ACT', number)
        const activityDrafts = { ...ui.activityDrafts }
        delete activityDrafts[action.reviewId]
        return log(
          {
            ...state,
            activities: [
              ...state.activities,
              {
                id,
                participantId: review.participantId,
                reviewId: review.id,
                title: draft.title.trim(),
                instructions: draft.instructions.trim(),
                assignedAt: at,
                status: 'assigned',
                reflection: '',
              },
            ],
            counters: { ...state.counters, activity: number },
            ui: { ...ui, activityDrafts },
          },
          review.participantId,
          `${config.reviewerRole} assigned ${id} “${draft.title.trim()}” from ${review.id}`,
          at,
        )
      }
      case 'setReflectionDraft':
        return { ...state, ui: { ...ui, reflectionDrafts: { ...ui.reflectionDrafts, [action.activityId]: action.text } } }
      case 'completeActivity': {
        const activity = state.activities.find((item) => item.id === action.activityId)
        const reflection = (ui.reflectionDrafts[action.activityId] ?? '').trim()
        if (!activity || activity.status === 'completed' || !reflection) return state
        const at = nowIso()
        const reflectionDrafts = { ...ui.reflectionDrafts }
        delete reflectionDrafts[action.activityId]
        return log(
          {
            ...state,
            activities: state.activities.map((item) =>
              item.id === activity.id ? { ...item, status: 'completed', reflection, completedAt: at } : item,
            ),
            ui: { ...ui, reflectionDrafts },
          },
          activity.participantId,
          `${participantName(config, activity.participantId)} completed ${activity.id} with a reflection`,
          at,
        )
      }
      case 'setDecisionDraft':
        return { ...state, ui: { ...ui, decisionDrafts: { ...ui.decisionDrafts, [action.participantId]: action.draft } } }
      case 'recordDecision': {
        const draft = ui.decisionDrafts[action.participantId]
        const reviewIds = state.reviews
          .filter((review) => review.participantId === action.participantId && review.outcome === 'reviewed')
          .map((review) => review.id)
        if (!draft?.outcome || !draft.reason.trim() || reviewIds.length === 0) return state
        const at = nowIso()
        const number = state.counters.decision + 1
        const id = formatId('DEC', number)
        const decisionDrafts = { ...ui.decisionDrafts }
        delete decisionDrafts[action.participantId]
        return log(
          {
            ...state,
            decisions: [
              ...state.decisions,
              { id, participantId: action.participantId, outcome: draft.outcome, reason: draft.reason.trim(), reviewIds, at },
            ],
            counters: { ...state.counters, decision: number },
            ui: { ...ui, decisionDrafts },
          },
          action.participantId,
          `Human decision ${id} for ${participantName(config, action.participantId)}: ${decisionLabels[draft.outcome]}`,
          at,
        )
      }
      case 'setReferralDraft':
        return { ...state, ui: { ...ui, referralDraft: { ...ui.referralDraft, ...action.draft } } }
      case 'createReferral': {
        const draft = ui.referralDraft
        if (Object.keys(validateReferralDraft(draft)).length > 0) return state
        const at = nowIso()
        const number = state.counters.referral + 1
        const id = formatId('REF', number)
        const partner = config.partners?.find((item) => item.id === draft.partnerId)
        return log(
          {
            ...state,
            referrals: [
              ...state.referrals,
              {
                id,
                participantId: draft.participantId,
                partnerId: draft.partnerId,
                purpose: draft.purpose.trim(),
                sharedInfo: draft.sharedInfo.trim(),
                consentConfirmed: true,
                status: 'pending',
                createdAt: at,
                updates: [{ status: 'pending', reason: 'Referral recorded with participant agreement.', at }],
              },
            ],
            counters: { ...state.counters, referral: number },
            ui: { ...ui, referralDraft: { participantId: '', partnerId: '', purpose: '', sharedInfo: '', consent: false } },
          },
          draft.participantId,
          `Referral ${id} to ${partner?.name ?? draft.partnerId} recorded for ${participantName(config, draft.participantId)}`,
          at,
        )
      }
      case 'setReferralUpdateDraft':
        return { ...state, ui: { ...ui, referralUpdateDrafts: { ...ui.referralUpdateDrafts, [action.referralId]: action.draft } } }
      case 'updateReferral': {
        const referral = state.referrals.find((item) => item.id === action.referralId)
        const draft = ui.referralUpdateDrafts[action.referralId]
        if (!referral || !draft?.status || draft.status === referral.status || !draft.reason.trim()) return state
        const at = nowIso()
        const status = draft.status
        const referralUpdateDrafts = { ...ui.referralUpdateDrafts }
        delete referralUpdateDrafts[action.referralId]
        return log(
          {
            ...state,
            referrals: state.referrals.map((item) =>
              item.id === referral.id
                ? { ...item, status, updates: [...item.updates, { status, reason: draft.reason.trim(), at }] }
                : item,
            ),
            ui: { ...ui, referralUpdateDrafts },
          },
          referral.participantId,
          `Referral ${referral.id} marked ${referralStatusLabels[status]}`,
          at,
        )
      }
      default:
        return state
    }
  }
}

export function devExport(config: DevDomainConfig, state: DevState) {
  return {
    exportedAt: nowIso(),
    domain: config.id,
    product: config.productLabel,
    notice: 'Interactive prototype · Fictional records. Criteria are illustrative task rubrics, not validated instruments.',
    participants: config.participants,
    criteria: config.criteria,
    tasks: config.tasks,
    submissions: state.submissions,
    reviews: state.reviews,
    activities: state.activities,
    ...(config.features.comparison ? { decisions: state.decisions } : {}),
    ...(config.features.referrals ? { partners: config.partners, referrals: state.referrals } : {}),
    history: state.history,
  }
}
